import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";

const CustomerAcquisitionMetrics = () => {
  const { customerOffersData } = useSelector(
    (state) => state?.merchantReducer
  );

  const [chartData, setChartData] = useState({
    series: [
      {
        name: "Total New Users",
        data: [],
      },
    ],
    categories: [],
  });
  const [maxDataValue, setMaxDataValue] = useState(10); // Initial max value
  const [step, setStep] = useState(2); // Initial step value

  const MONTHS_ORDER = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  const convertMonthToAbbreviation = (monthString) => {
    const date = new Date(monthString + "-01");
    return date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  };

  useEffect(() => {
    if (customerOffersData?.success && customerOffersData?.data?.customerMetrics) {
      const metrics = customerOffersData.data.customerMetrics;

      // Convert months to abbreviations and format the data
      const formattedData = metrics.map((item) => ({
        month: convertMonthToAbbreviation(item.month),
        totalNewUsers: item.totalNewUsers,
      }));

      // Sort the formatted data based on the predefined month order
      const sortedData = formattedData.sort(
        (a, b) =>
          MONTHS_ORDER.indexOf(a.month) - MONTHS_ORDER.indexOf(b.month)
      );

      const dataValues = sortedData.map((item) => item.totalNewUsers);

      // Calculate max value and step dynamically
      const calculatedMaxDataValue = Math.max(...dataValues, 10);
      const calculatedStep = Math.ceil(calculatedMaxDataValue / 5);

      setMaxDataValue(calculatedMaxDataValue);
      setStep(calculatedStep);

      // Update chart data
      setChartData({
        series: [
          {
            name: "Total New Users",
            data: dataValues,
          },
        ],
        categories: sortedData.map((item) => item.month),
      });
    }
  }, [customerOffersData]);

  const options = {
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
    },
    colors: ["#00ADEF"],
    chart: {
      fontFamily: "Poppins, sans-serif",
      height: 335,
      type: "area",
      dropShadow: {
        enabled: true,
        color: "#623CEA14",
        top: 10,
        blur: 4,
        left: 0,
        opacity: 0.1,
      },
      toolbar: {
        show: false,
      },
    },
    responsive: [
      {
        breakpoint: 575,
        options: {
          chart: {
            height: 300,
          },
          xaxis: {
            labels: {
              rotate: -45,
              style: {
                fontSize: "10px",
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 300,
          },
        },
      },
      {
        breakpoint: 1366,
        options: {
          chart: {
            height: 350,
          },
        },
      },
    ],
    stroke: {
      width: 2,
      curve: "smooth",
    },
    grid: {
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 4,
      colors: "#fff",
      strokeColors: ["#00ADEF"],
      strokeWidth: 3,
      strokeOpacity: 0.9,
      strokeDashArray: 0,
      fillOpacity: 1,
      hover: {
        size: undefined,
        sizeOffset: 5,
      },
    },
    xaxis: {
      type: "category",
      categories: chartData.categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      title: {
        text: "Time Period (Months)",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxDataValue / step) * step,
      tickAmount: Math.ceil(maxDataValue / step),
      labels: {
        formatter: (value) => value,
      },
      title: {
        text: "New Users",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
  };

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 pt-7.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col sm:flex-col xl:flex-row justify-between w-full mt-4">
        <span className="text-[18px] sm:text-[20px] font-poppins font-[600] text-[black] leading-tight flex-1">
          Customer Acquisition Metrics
        </span>
      </div>

      {chartData.series[0]?.data.length > 0 ? (
        <div>
          <div id="chartOne" className="-ml-5 mt-4">
            <ReactApexChart
              options={options}
              series={chartData.series}
              type="area"
              height={350}
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-[200px] flex items-center justify-center">
          <div id="chartTwo" className="min-w-0 md:min-w-[700px]">
            <div className="text-center text-gray-500 text-lg font-medium py-10">
              No New Users Found
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAcquisitionMetrics;
